import React, {useEffect, useState} from 'react';
import clsx from 'clsx';

import {
  NavigationMapPointerIcon,
  CloseMenuIcon,
  SparklesIcon,
  useAppContext,
} from '~/components';
import {useQuery} from 'react-query';
import {useGeolocation} from '~/hooks';
import {queryBe} from '~/utils';
import {GetPostcodeDocument} from '~/lib/types/graphql/api';

type Props = {
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  placeholder?: string;
  containerClassName?: string;
  inputClassName?: string;
  buttonClassName?: string;
  postalCodeFetchError?: string;
  showEmptyFieldError?: boolean;
  hasResults?: boolean;
};

export function PostalSearchForm({
  onSubmit,
  placeholder = 'Enter 6-digit postal code to view timeslots',
  containerClassName = '',
  inputClassName = '',
  buttonClassName = '',
  postalCodeFetchError = '',
  showEmptyFieldError = false,
  hasResults = false,
}: Props) {
  const {data: geolocation, isLoading: isLoadingGeolocation} = useGeolocation();
  //Set postcode form geolocation if we can
  //Eslint picking up the wrong useQuery
  // eslint-disable-next-line hydrogen/client-component-banned-hooks
  useQuery(
    ['geo-postcode', geolocation],
    async () => {
      // Get customer
      const {data, errors} = await queryBe<
        'postcode',
        (position: [number, number]) => string
      >({
        caller: 'client',
        query: GetPostcodeDocument,
        variables: {
          position: [geolocation!.longitude, geolocation!.latitude],
        },
      });
      if (errors) {
        throw new Error('Error getting postcode');
      }
      return data.postcode;
    },
    {
      enabled: geolocation != null,

      onSuccess: (postcode) => {
        //Only autofill postcode if they havent already entered one
        if (searchPostcode.length == 0) {
          setSearchPostcode(postcode);
        }
      },

      refetchOnWindowFocus: false,
    },
  );

  const {searchPostcode, setSearchPostcode} = useAppContext();
  const [focused, setFocused] = useState(false);

  //Necessary since without it, any input before react finishes setting up is wiped out afterwards
  //so we work around this by disabling the input until react is ready
  const [inputEnabled, setInputEnabled] = useState(false);

  useEffect(() => {
    setInputEnabled(true);
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // this function is here so that it stops clearing out the postcode on enter
    if (event.key === 'Enter') event.preventDefault();
  };

  // Derived state for error messages
  const invalidPostcode =
    searchPostcode.length < 6 && searchPostcode.length > 0;
  const showPostalError =
    postalCodeFetchError && searchPostcode.length === 6 && !hasResults;
  const emptyPostcode = searchPostcode.length === 0 && showEmptyFieldError;

  if (showPostalError) {
    console.error('Postal Code Error', postalCodeFetchError);
  }

  return (
    <div className={clsx(containerClassName, 'flex-col')}>
      <form
        className={clsx(
          'transition-duration-300 flex h-[45px] items-center rounded-full border-[0.5px] border-acai-900 px-4 pr-2 transition-colors',
          inputEnabled ? 'bg-clay-100' : 'bg-clay-50',
          (invalidPostcode || showPostalError || emptyPostcode) &&
            'border-[1px] border-red-700',
        )}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.(e);
        }}
      >
        <NavigationMapPointerIcon
          fill={
            invalidPostcode || showPostalError || emptyPostcode ? '#B91c1c' : ''
          }
        />
        <input
          type="number"
          name="search"
          className={clsx(
            inputClassName,
            'h-full flex-grow bg-transparent px-1 text-[13px] leading-[13px] text-acai-900 placeholder:text-[13px] placeholder:text-acai-900 placeholder:transition-colors placeholder:!duration-300',
            'focus:outline-none focus:ring-0',
            'focus-within:outline-none focus-within:ring-0',
            'focus-visible:outline-none focus-visible:ring-0',
            'active:outline-none active:ring-0',
            'disabled:placeholder:text-transparent',
            emptyPostcode && 'placeholder:text-red-700',
          )}
          disabled={!inputEnabled}
          placeholder={
            isLoadingGeolocation ? 'Loading your postcode' : placeholder
          }
          autoComplete="off"
          value={searchPostcode}
          onChange={(e) =>
            // Regex to only allow numbers and max 6 digits
            new RegExp('^[0-9]{0,6}$').test(e.currentTarget.value) &&
            setSearchPostcode(e.currentTarget.value)
          }
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => handleKeyDown(e)}
        />

        {isLoadingGeolocation && (
          <SparklesIcon
            width={12}
            height={12}
            fill="#FA685B"
            className="animate-pulse"
          />
        )}

        {!isLoadingGeolocation && searchPostcode.length > 2 && (
          <button
            onClick={() => setSearchPostcode('')}
            className={clsx(
              buttonClassName,
              'flex h-[35px] w-[35px] items-center justify-center rounded-full bg-foam-100',
            )}
          >
            <CloseMenuIcon
              width={12}
              height={12}
              stroke={focused ? '#593B37' : '#ACACAC'}
            />
          </button>
        )}
      </form>

      {invalidPostcode && (
        <div className="px-2 pt-2 text-sm text-red-700">
          Enter 6-digit postal code to view timeslots
        </div>
      )}
      {showPostalError && (
        <div className="px-2 pt-2 text-sm text-red-700">
          No location found, enter another 6-digit postal code.
        </div>
      )}
    </div>
  );
}
